import {getProjectBySlug, projectItems} from '../data/projects';
import {BaseRoute, Route, routes} from '../types/routes';

export const SITE_URL = 'https://ufat.org';

const DEFAULT_IMAGE_URL = `${SITE_URL}/social-preview.png`;
const DEFAULT_IMAGE_ALT = 'UFAT logo on an abstract dark atmospheric background.';
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

export interface SeoMetadata {
  readonly canonicalUrl: string;
  readonly description: string;
  readonly imageAlt: string;
  readonly imageUrl: string;
  readonly robots: string;
  readonly title: string;
}

export const indexableRoutes: Route[] = [
  ...routes.filter((route) => route !== '/portal'),
  ...projectItems
    .filter((project) => project.visible)
    .map((project): Route => `/projects/${project.slug}`)
];

const routeMetadata: Record<BaseRoute, Pick<SeoMetadata, 'description' | 'robots' | 'title'>> = {
  '/': {
    description: 'UFAT is a Chicago arts and technology nonprofit building creative culture through education, events, digital platforms, and community programs.',
    robots: DEFAULT_ROBOTS,
    title: 'UFAT | Arts, Technology & Creative Culture'
  },
  '/about': {
    description: 'Learn how UFAT connects storytelling, publishing, software, education, and community programs to build creative experiences for the future.',
    robots: DEFAULT_ROBOTS,
    title: 'About UFAT | Arts, Technology & Community'
  },
  '/coding': {
    description: 'Discover UFAT Labs software, open-source frameworks, web applications, and scalable technology built for creators and connected digital experiences.',
    robots: DEFAULT_ROBOTS,
    title: 'Software & Open Source | UFAT'
  },
  '/creators': {
    description: 'Pitch your manuscript, illustrated book, or story-driven media project to UFAT Studios and explore our creator publishing process.',
    robots: DEFAULT_ROBOTS,
    title: 'Submit Your Work | UFAT Studios'
  },
  '/portal': {
    description: 'Access the UFAT Studios creator portal.',
    robots: 'noindex, nofollow',
    title: 'Creator Portal | UFAT Studios'
  },
  '/projects': {
    description: 'Explore UFAT books, stories, open-source frameworks, and emerging platforms spanning publishing, software, education, and creative technology.',
    robots: DEFAULT_ROBOTS,
    title: 'Projects | UFAT'
  },
  '/publishing': {
    description: 'Explore UFAT Studios publishing services for hardcover, paperback, ebook, and story-driven media projects.',
    robots: DEFAULT_ROBOTS,
    title: 'Publishing with UFAT Studios | Books & Digital Media'
  },
  '/support': {
    description: 'Contact UFAT for publishing, technology, project, and general support.',
    robots: DEFAULT_ROBOTS,
    title: 'Contact & Support | UFAT'
  }
};

const toCanonicalUrl = (route: Route) => `${SITE_URL}${route === '/' ? '/' : route}`;

export const getSeoMetadata = (route: Route): SeoMetadata => {
  if(route.startsWith('/projects/')) {
    const slug = route.slice('/projects/'.length);
    const project = getProjectBySlug(slug);

    if(project?.visible) {
      return {
        canonicalUrl: toCanonicalUrl(route),
        description: project.body,
        imageAlt: DEFAULT_IMAGE_ALT,
        imageUrl: DEFAULT_IMAGE_URL,
        robots: DEFAULT_ROBOTS,
        title: `${project.title} | UFAT Projects`
      };
    }

    return {
      canonicalUrl: toCanonicalUrl(route),
      description: 'Explore arts, publishing, software, and creative technology projects from UFAT.',
      imageAlt: DEFAULT_IMAGE_ALT,
      imageUrl: DEFAULT_IMAGE_URL,
      robots: 'noindex, nofollow',
      title: 'Project | UFAT'
    };
  }

  return {
    ...routeMetadata[route as BaseRoute],
    canonicalUrl: toCanonicalUrl(route),
    imageAlt: DEFAULT_IMAGE_ALT,
    imageUrl: DEFAULT_IMAGE_URL
  };
};

const setMeta = (attribute: 'name' | 'property', key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if(!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
};

const setRouteStructuredData = (metadata: SeoMetadata) => {
  let element = document.head.querySelector<HTMLScriptElement>('#route-structured-data');
  if(!element) {
    element = document.createElement('script');
    element.id = 'route-structured-data';
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  const isProject = metadata.canonicalUrl.includes('/projects/');
  const page = {
    '@type': 'WebPage',
    '@id': `${metadata.canonicalUrl}#webpage`,
    url: metadata.canonicalUrl,
    name: metadata.title,
    description: metadata.description,
    isPartOf: {'@id': `${SITE_URL}/#website`},
    inLanguage: 'en-US'
  };

  element.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': isProject
      ? [
          page,
          {
            '@type': 'BreadcrumbList',
            '@id': `${metadata.canonicalUrl}#breadcrumb`,
            itemListElement: [
              {'@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/`},
              {'@type': 'ListItem', position: 2, name: 'Projects', item: `${SITE_URL}/projects`},
              {'@type': 'ListItem', position: 3, name: metadata.title, item: metadata.canonicalUrl}
            ]
          }
        ]
      : [page]
  });
};

export const applySeoMetadata = (metadata: SeoMetadata) => {
  document.title = metadata.title;
  setMeta('name', 'description', metadata.description);
  setMeta('name', 'robots', metadata.robots);
  setMeta('property', 'og:description', metadata.description);
  setMeta('property', 'og:image', metadata.imageUrl);
  setMeta('property', 'og:image:alt', metadata.imageAlt);
  setMeta('property', 'og:image:secure_url', metadata.imageUrl);
  setMeta('property', 'og:title', metadata.title);
  setMeta('property', 'og:url', metadata.canonicalUrl);
  setMeta('name', 'twitter:description', metadata.description);
  setMeta('name', 'twitter:image', metadata.imageUrl);
  setMeta('name', 'twitter:image:alt', metadata.imageAlt);
  setMeta('name', 'twitter:title', metadata.title);
  setRouteStructuredData(metadata);

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if(!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = metadata.canonicalUrl;
};
